import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getCategoryColors } from "@/lib/categoryColors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Clock, Zap, Wrench, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useAnnonces, getUrgencyLabel, getCategoryLabel } from "@/hooks/useAnnonces";
import { useArtisanProfile } from "@/hooks/useArtisanProfile";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";

const DashboardNearbyAnnonces = () => {
  const { t, locale } = useLanguage("artisan");
  const lp = useLocalizedPath();
  const { data: profile } = useArtisanProfile();
  const { data: annonces = [], isLoading } = useAnnonces({
    wilaya: profile?.wilaya,
  });

  // Show only the 5 most recent
  const recent = annonces.slice(0, 5);

  if (isLoading) {
    return (
      <section className="space-y-3">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          {t("nearby_annonces.title")}
        </h2>
        <div className="py-6 text-center text-sm text-muted-foreground">{t("nearby_annonces.loading")}</div>
      </section>
    );
  }

  const count = annonces.length;
  const countLabel = count === 1
    ? t("nearby_annonces.count_one", { count })
    : t("nearby_annonces.count_other", { count });

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          {t("nearby_annonces.title")}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{countLabel}</span>
          {annonces.length > 0 && (
            <Link to={lp("/annonces")}>
              <Button variant="ghost" size="sm" className="text-xs">{t("nearby_annonces.see_more")}</Button>
            </Link>
          )}
        </div>
      </div>

      {recent.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center flex flex-col items-center gap-2">
            <Search className="h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm font-medium text-foreground">
              {profile?.wilaya
                ? `Aucune demande à ${profile.wilaya} pour le moment.`
                : "Aucune demande dans votre zone pour le moment."}
            </p>
            <p className="text-xs text-muted-foreground">
              Complétez votre profil et revenez bientôt — les demandes arrivent !
            </p>
            <Link to={lp("/annonces")} className="mt-1">
              <Button variant="outline" size="sm" className="text-xs">
                Voir toutes les demandes
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {recent.map((annonce) => {
            const catKey = annonce.category || annonce.metier;
            const catColors = getCategoryColors(catKey);
            return (
            <Card key={annonce.id} className={cn("group overflow-hidden transition-all hover:shadow-md hover:border-primary/20 border-s-4", catColors.border)}>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={cn("text-xs border", catColors.badgeBg, catColors.badgeText)}>
                      <Wrench className="me-1 h-3 w-3" />
                      {getCategoryLabel(catKey, locale)}
                    </Badge>
                    {annonce.urgency === "asap" && (
                      <Badge className="bg-destructive text-destructive-foreground text-xs">
                        <Zap className="me-1 h-3 w-3" />{t("nearby_annonces.urgent")}
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-display text-sm font-semibold leading-tight line-clamp-1">
                    {annonce.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {annonce.commune}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {getUrgencyLabel(annonce.urgency, locale)}
                    </span>
                    {(annonce.budget_min || annonce.budget_max) && (
                      <span className="flex items-center gap-1 font-medium text-foreground">
                        <span className="font-bold text-[10px] leading-none">DA</span>
                        {annonce.budget_min && annonce.budget_max
                          ? `${annonce.budget_min.toLocaleString()} - ${annonce.budget_max.toLocaleString()} DA`
                          : annonce.budget_min
                          ? `${annonce.budget_min.toLocaleString()} DA`
                          : `${annonce.budget_max!.toLocaleString()} DA`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end border-t pt-2">
                  <Link to={lp(`/annonce/${annonce.id}`)}>
                    <Button variant="ghost" size="sm" className="text-xs h-7">
                      {t("nearby_annonces.open")} <DirectionalArrow className="ms-1 h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default DashboardNearbyAnnonces;
